<template>
  <form class="container mx-auto px-4 sm:px-6 lg:px-8">
    <div class="flex text-white text-xl justify-center my-10">
      Veuillez compléter ce formulaire pour créer un compte
    </div>
    <div class="md:grid md:grid-cols-2">
      <div class="hidden md:block md:h-full md:w-[384px] mx-auto">
        <dot-lottie-vue
          src="animations/73812-cloud-computing-security.json"
          style="width: 100%; height: 100%"
          autoplay
          loop
        />
      </div>
      <div class="max-w-xl">
        <div class="flex flex-row flex-wrap mt-4 mb-2 gap-10 text-sm text-white">
          <div class="flex flex-row items-center" @click="gender = 0">
            <input
              id="man"
              v-model="gender"
              value="0"
              type="radio"
              class="border-2 border-white mr-2"
            >
            <label
              for="man"
              class="flex flex-row items-center cursor-pointer"
            >
              <img
                :class="['h-16 mr-2', gender === 0 ? 'rounded-full border-white border-2' : 'border-0']"
                :src="profileMale"
              >
              Homme
            </label>
          </div>
          <br>
          <div class="flex flex-row items-center" @click="gender = 1">
            <input
              id="woman"
              v-model="gender"
              value="1"
              type="radio"
              class="border-2 border-white mr-2"
            >
            <label
              for="woman"
              class="flex flex-row items-center cursor-pointer"
            >
              <img
                :class="['h-16 mr-2', gender === 1 ? 'rounded-full border-white border-2' : 'border-0']"
                :src="profileFemale"
              >
              Femme
            </label>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-10">
          <RegisterInput
            v-model="firstname"
            :error="errors.firstname"
            type="text"
            placeholder="Prénom"
          />
          <RegisterInput
            v-model="lastname"
            :error="errors.lastname"
            type="text"
            placeholder="Nom"
          />
        </div>
        <div v-if="errors.gender" class="mt-2 text-sm text-orange-light">
          {{ errors.gender }}
        </div>

        <RegisterInput
          v-model="postalCode"
          :error="errors.postalCode"
          type="text"
          placeholder="Code Postal de votre pharmacie"
          @blur="formatZipcode"
          @input="() => debounceFormatZipcode(10000)"
        />

        <RegisterInput
          v-model="email"
          :error="errors.email"
          type="email"
          placeholder="Email"
          @blur="verifyEmail"
        />

        <div class="flex flex-1 flex-col py-4">
          <div class="grid grid-cols-2 w-full gap-10">
            <RegisterInput
              v-model="password"
              type="password"
              placeholder="Mot de passe"
            />
            <RegisterInput
              v-model="passwordConfirmation"
              :error="errors.passwordConfirmation"
              type="password"
              placeholder="Confirmez votre mot de passe"
            />
          </div>
          <div :class="['mt-2 text-sm', errors.password ? 'text-orange-light' : 'text-gray-300']">
            {{ errors.password || passwordInfo }}
          </div>
        </div>

        <div :class="['mt-1 relative', errors.lgo ? 'border-orange-light' : '']">
          <v-combobox
            v-model="lgo"
            label="Choisissez votre LGO"
            class="text-white"
            variant="underlined"
            placeholder="Aucun lgo. Cliquez ici pour chercher..."
            :items="lgos"
            :item-title="(item: any) => item.name"
          />
        </div>
        <div v-if="errors.lgo" class="mt-2 text-sm text-orange-light">
          {{ errors.lgo }}
        </div>

        <v-combobox
          v-model="grouping"
          label="Choisissez votre groupement"
          class="text-white"
          variant="underlined"
          placeholder="Aucun groupement. Cliquez ici pour chercher..."
          :items="[{ name: 'Aucun groupement', id: 0 }, ...groupings]"
          hint="Recherchez votre groupement en saisissant son nom ou en parcourant la liste, puis sélectionnez-le"
          :item-title="(item: any) => item.name"
        />

        <div class="my-4">
          <ReferralRegisterInput v-model="referral" v-model:error="errors.referral" />
        </div>

        <div class="text-gray-200 my-2">
          <input
            v-model="checkedCGU"
            type="checkbox"
            class="accent-secondary mr-2"
          >
          j'ai lu et j'accepte les
          <a
            href="https://api.apodispharma.com/contracts/terms/show"
            target="_blank"
            class="underline"
          >
            CGU
          </a>
        </div>
        <div class="text-gray-200 my-2">
          <input
            v-model="checkedTERMS"
            type="checkbox"
            class="accent-secondary mr-2"
          >
          j'ai lu et j'accepte la
          <a
            href="https://api.apodispharma.com/contracts/privacy-policy/show"
            target="_blank"
            class="underline"
          >
            politique de confidentialité des données
          </a>
        </div>
      </div>
    </div>

    <BaseAlert v-if="errors.page" type="error" title="Erreur">
      {{ errors.page }}
    </BaseAlert>

    <button
      type="button"
      class="flex justify-center mx-auto py-3 px-7 mt-8 text-xl text-primary-dark rounded-full bg-secondary"
      @click="validate"
    >
      Créer mon compte
    </button>
  </form>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from 'vue';
import {groupingsApi, lgoApi} from '@/container';
import BaseAlert from '@/core/components/base/BaseAlert.vue';
import RegisterInput from '@/register/views/RegisterInput.vue';
import {DotLottieVue} from '@lottiefiles/dotlottie-vue';
import type {Grouping} from '@/core/interfaces/laboratory/Grouping';
import profileMale from '@/assets/images/profile-male.png';
import profileFemale from '@/assets/images/profile-female.png';
import ReferralRegisterInput from '@/register/components/ReferralRegisterInput.vue';
import {ProSanteConnectUserInfo} from "@/core/interfaces/pro-sante-connect";
import usePostalCode from "@/register/composables/usePostalCode";
import useEmailVerification from "@/register/composables/useEmailVerification";
import usePassword from "@/register/composables/usePassword";
import {useRegisterValidation} from "@/register/composables/useRegisterValidation";
import useReferral from "@/register/composables/useReferral";
import LgoInfos from "@/core/interfaces/LgoInfos";

const props = withDefaults(defineProps<{
    ecps?: ProSanteConnectUserInfo | null
}>(), {
    ecps: null
});

const emit = defineEmits<{
    validate: [user: {
        firstname: string | undefined
        lastname: string | undefined
        gender: number | undefined
        email: string | undefined
        password: string | undefined
        lgo: number | null
        referral: string | undefined
        postalCode: string | undefined
        groupingId: number | null
    }]
}>();

const firstname = ref<string | undefined>(undefined);
const lastname = ref<string | undefined>(undefined);
const gender = ref<number | undefined>(undefined);
const lgo = ref<LgoInfos | null>(null);
const checkedCGU = ref(false);
const checkedTERMS = ref(false);
const grouping = ref<Grouping | null>(null);

const {referral, referralError, verifyReferral} = useReferral();
const {postalCode, formatZipcode, debounceFormatZipcode} = usePostalCode();
const {email, emailError, alreadyUsedEmail, isEmailValid, verifyEmail} = useEmailVerification();
const {password, passwordConfirmation, isPasswordValid, passwordInfo, passwordsMatch} = usePassword();
const {errors, submitEnabled, runValidationRules} = useRegisterValidation({
    firstname,
    lastname,
    gender,
    postalCode,
    isEmailValid,
    alreadyUsedEmail,
    lgo,
    isPasswordValid,
    passwordsMatch,
    passwordInfo,
    checkedCGU,
    checkedTERMS
});

const lgos = ref<LgoInfos[]>([]);
const groupings = ref<Grouping[]>([]);

watch(emailError, value => errors.email = value);
watch(referralError, value => errors.referral = value);

email.value = import.meta.env.VITE_LOGIN_EMAIL ?? email.value;
password.value = import.meta.env.VITE_LOGIN_PASSWORD ?? password.value;

const buildUser = () => {
    return {
        firstname: firstname.value,
        lastname: lastname.value,
        gender: gender.value,
        email: email.value,
        password: password.value,
        lgo: lgo.value?.id ?? null,
        referral: referral.value,
        postalCode: postalCode.value,
        groupingId: grouping.value?.id !== 0 ? grouping.value?.id ?? null : null
    };
};

/**
 * Auto complete values from ecps info.
 */
const autocompleteECPS = () => {
    if (props.ecps) {
        if (firstname.value === undefined && props.ecps.given_name) {
            firstname.value = props.ecps.given_name;
        }

        if (lastname.value === undefined && props.ecps.family_name) {
            lastname.value = props.ecps.family_name;
        }

        if(gender.value === undefined && props.ecps.codeCivilite) {
            switch (props.ecps.codeCivilite) {
                case "M":
                    gender.value = 0;
                    break;
                case "MME":
                    gender.value = 1;
                    break;
                default:
                    gender.value = undefined;
            }
        }

        if (
            email.value === undefined &&
            props.ecps.SubjectRefPro?.exercices?.length &&
            props.ecps.SubjectRefPro.exercices[0]?.activities?.length &&
            props.ecps.SubjectRefPro.exercices[0].activities[0].adresseEMail
        ) {
            email.value = props.ecps.SubjectRefPro.exercices[0].activities[0].adresseEMail;
        }
    }
};

const validate = async () => {
    const referralValid = await verifyReferral();

    if (!referralValid) {
        return;
    }

    if (submitEnabled.value) {
        emit('validate', buildUser());
        return;
    }

    runValidationRules();
};

onMounted(() => {
    email.value = import.meta.env.VITE_LOGIN_EMAIL ?? '';
    password.value = import.meta.env.VITE_LOGIN_PASSWORD ?? '';

    lgoApi().getList().then(list => {
        lgos.value = list.lgo;
    });

    groupingsApi().index().then(response => {
        groupings.value = response;
    });

    autocompleteECPS();
});
</script>

<style scoped>
:deep(.v-field__input input) {
    background-color: transparent;
    color: white;
}

:deep(.v-field__input input:focus) {
    border-color: transparent;
    --tw-ring-color: transparent;
}

:deep(.v-input--density-default) {
    --v-input-padding-top: 10px;
}

:deep(.v-messages) {
    padding-top: 4px;
    font-size: 14px;
    font-family: "Poppins-SemiBold";
    color: #65efca;
    opacity: 1;
}

:deep(.v-messages__message) {
    line-height: 22px;
}
</style>
